import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Foundation } from '../types/Foundation';
import * as apiService from '../services/apiServiceFactory';

// Number of items to show per page
export const ITEMS_PER_PAGE = 30;

export function useSupabaseFoundationSearch() {
  // Store search results
  const [searchResults, setSearchResults] = useState<Foundation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentQuery, setCurrentQuery] = useState('');
  const [totalFoundations, setTotalFoundations] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAllFoundations(1, ITEMS_PER_PAGE);
      
      if (!response || !response.foundations || response.foundations.length === 0) {
        throw new Error('Ingen data tillgänglig');
      }

      // Set total foundations count and pagination info
      setTotalFoundations(response.total);
      setSearchResults(response.foundations);
      setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE));
      setHasMore(response.hasMore);
      setCurrentPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ett okänt fel uppstod');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Search function that uses the Supabase API
  const searchFoundationsHandler = useCallback(async (query: string, page: number = 1, region?: string) => {
    setLoading(true);
    setCurrentQuery(query);
    setError(null);
    
    try {
      // Always pass query (even if empty) and region to the API
      // This allows searching by region only when query is empty
      const response = await apiService.searchFoundations(query, page, ITEMS_PER_PAGE, region);
      
      setSearchResults(response.foundations);
      setTotalFoundations(response.total);
      setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE));
      setHasMore(response.hasMore);
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sökfel uppstod');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Move to next page
  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const nextPageNum = currentPage + 1;
      setCurrentPage(nextPageNum);
      
      // Fetch the next page of results
      if (currentQuery) {
        searchFoundationsHandler(currentQuery, nextPageNum);
      } else {
        apiService.getAllFoundations(nextPageNum, ITEMS_PER_PAGE)
          .then(response => {
            setSearchResults(response.foundations);
            setHasMore(response.hasMore);
          })
          .catch(err => {
            setError(err instanceof Error ? err.message : 'Fel vid hämtning av nästa sida');
          });
      }
    }
  }, [currentPage, totalPages, currentQuery, searchFoundationsHandler]);

  // Move to previous page
  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      const prevPageNum = currentPage - 1;
      setCurrentPage(prevPageNum);
      
      // Fetch the previous page of results
      if (currentQuery) {
        searchFoundationsHandler(currentQuery, prevPageNum);
      } else {
        apiService.getAllFoundations(prevPageNum, ITEMS_PER_PAGE)
          .then(response => {
            setSearchResults(response.foundations);
            setHasMore(response.hasMore);
          })
          .catch(err => {
            setError(err instanceof Error ? err.message : 'Fel vid hämtning av föregående sida');
          });
      }
    }
  }, [currentPage, currentQuery, searchFoundationsHandler]);

  // Go to specific page
  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      
      // Fetch the specified page of results
      if (currentQuery) {
        searchFoundationsHandler(currentQuery, page);
      } else {
        apiService.getAllFoundations(page, ITEMS_PER_PAGE)
          .then(response => {
            setSearchResults(response.foundations);
            setHasMore(response.hasMore);
          })
          .catch(err => {
            setError(err instanceof Error ? err.message : 'Fel vid hämtning av sida');
          });
      }
    }
  }, [totalPages, currentQuery, searchFoundationsHandler]);

  // Refresh data manually
  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Trigger a manual refresh of the database
      await apiService.refreshData();
      
      // Then reload the current view
      if (currentQuery) {
        await searchFoundationsHandler(currentQuery, currentPage);
      } else {
        await loadData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fel vid uppdatering av data');
    } finally {
      setLoading(false);
    }
  }, [currentQuery, currentPage, searchFoundationsHandler, loadData]);

  return {
    searchResults,
    totalFoundations,
    totalResults: searchResults.length,
    loading,
    error,
    searchFoundations: searchFoundationsHandler,
    currentPage,
    totalPages,
    nextPage,
    previousPage,
    goToPage,
    retryFetch: loadData,
    currentQuery,
    hasMore,
    refreshData
  };
}
